/* ──────────────────────────────────────────────────────────────────────
 * QuickActionsCard  —  Section 2 of the redesigned Lead Actions panel
 *
 * Replaces the generic "Communication Log" + monolithic "LOG ACTIVITY"
 * button with five dedicated workflows. Each action is its own tile;
 * clicking one expands an inline form below the grid with its own save
 * button so the operator always knows exactly what they're committing.
 *
 *   📞 Log Call          → outcome pills + notes  → "Save Call"
 *   📝 Add Note          → notes textarea         → "Save Note"
 *   📧 Send Email        → subject + body + files → "Send Email"
 *   🤝 Log Meeting       → outcome subset + notes → "Save Meeting"
 *   ⏰ Schedule Follow-up → date/time + notes      → "Create Follow-up"
 *
 * Smart status suggestion: after a Call is saved with a positive or
 * negative outcome, an inline banner suggests an appropriate status
 * change — never forces it.
 *
 * Backend wiring (no schema changes — uses existing endpoints):
 *   Log Call / Meeting   → POST /lead-contacts
 *   Add Note             → POST /leads/:id/notes
 *   Send Email           → POST /leads/:id/email
 *   Schedule Follow-up   → POST /tasks
 * ──────────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import {
  Box, Typography, Stack, Button, TextField, Alert, Collapse, Chip, IconButton,
  Menu, MenuItem, ListItemText, CircularProgress, Snackbar,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import EditNoteIcon from "@mui/icons-material/EditNote";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import PremiumDateTimePicker from "../../Components/PremiumDateTimePicker";
import { logContact, OUTCOME_OPTIONS } from "../Tasks/taskApi";
import { addLeadNote, sendEmailFromLead, sendSmsFromLead, generateSmsAiDraft, fetchCustomerMessageTemplates, updateLead, type LeadThread, type LeadStatus, type CustomerMessageTemplate } from "./leadApi";
import { leadConfig, resolveInquirySubject } from "./leadVertical";
import EmailComposerToolbar from "../../Components/EmailComposer/EmailComposerToolbar";
import CommunicationTemplateToolbar from "../../Components/EmailComposer/CommunicationTemplateToolbar";
import AttachmentDropzone from "../../Components/EmailComposer/AttachmentDropzone";
import { createTask } from "../Tasks/taskApi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Quill represents an empty editor as "<p><br></p>" — strip tags before
// length-checking so the Send button doesn't enable on an empty draft.
const isBodyEmpty = (html: string): boolean =>
  !html || html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim().length === 0;

const NAVY = "#16233A";
const GOLD = "#7C4DFF";

type ActionKey = null | "call" | "note" | "email" | "meeting" | "follow_up" | "sms";

// Outcomes shown for meetings — narrower set; meetings rarely "no answer".
const MEETING_OUTCOMES = OUTCOME_OPTIONS.filter((o) =>
  ["contacted", "interested", "not_interested", "callback_requested"].includes(o.value)
);

// Outcomes that should trigger a smart status suggestion after a call.
const CALL_SUGGEST: Record<string, { status: LeadStatus; label: string }> = {
  contacted:      { status: "contacted", label: "Update lead status to Contacted?" },
  interested:     { status: "contacted", label: "Mark this lead as Contacted?" },
  not_interested: { status: "lost",      label: "Move this lead to Lost?" },
};

interface TeamUserLike {
  id: number;
  display_name?: string | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface Props {
  lead: LeadThread;
  teamUsers: TeamUserLike[];
  onChanged?: () => void;
  /** Read-only historical mode. Replaces the action tiles with a muted
   *  placeholder so previous assignees still see the section heading
   *  (visual continuity with the rest of the page) but cannot trigger
   *  any communication / follow-up creation. */
  readOnly?: boolean;
}

export default function QuickActionsCard({ lead, teamUsers, onChanged, readOnly = false }: Props) {
  const [active, setActive] = useState<ActionKey>(null);

  // Shared transient state — surfaces post-save banners + smart suggestions.
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [suggest, setSuggest] = useState<{ status: LeadStatus; label: string } | null>(null);

  // Per-form state — kept on the parent so toggling tiles doesn't lose
  // typed input (operators routinely flip between Call ↔ Note while
  // they figure out what they actually want to record).
  const [callOutcome, setCallOutcome] = useState("");
  const [callNotes, setCallNotes]     = useState("");
  /* template_id is persisted on the activity row so analytics can
     attribute usage. Set by the shared CommunicationTemplateToolbar
     when the rep applies a template; cleared by reset(). */
  const [callTemplateId, setCallTemplateId] = useState<number | null>(null);

  const [noteText, setNoteText] = useState("");
  const [noteTemplateId, setNoteTemplateId] = useState<number | null>(null);

  const [emailTo, setEmailTo]                   = useState<string>(lead.email ?? "");
  const [emailSubject, setEmailSubject]         = useState("");
  const [emailBody, setEmailBody]               = useState("");
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  /* Template + AI-assisted flags surface in the email activity log
     and the lead Timeline. Set by the shared toolbar; cleared by reset. */
  const [emailTemplateId, setEmailTemplateId]   = useState<number | null>(null);
  const [emailAiAssisted, setEmailAiAssisted]   = useState<boolean>(false);

  const [meetOutcome, setMeetOutcome] = useState("");
  const [meetNotes, setMeetNotes]     = useState("");
  const [meetTemplateId, setMeetTemplateId] = useState<number | null>(null);

  const [fuDate, setFuDate]         = useState("");
  const [fuAssignee, setFuAssignee] = useState<number | "">(lead.assigned_to ?? "");
  const [fuPriority, setFuPriority] = useState<"low" | "medium" | "high" | "urgent">("medium");
  const [fuNotes, setFuNotes]       = useState("");

  /* SMS composer state — Phase 2.A/B/C. Body is plain text only.
   * Live char + segment counter. Template picker (Phase 2.B) pulls
   * customer_message templates from the shared email_templates table.
   * AI Draft button (Phase 2.C) hits Claude via /sms/ai-draft. Merge
   * variables are resolved CLIENT-SIDE from the lead context — no
   * round-trip to the backend template resolver. */
  const [smsBody, setSmsBody] = useState("");
  /** Editable destination number — default-fills from the lead's phone
   *  but the rep can change it (alternate / spouse / corrected typo
   *  per the SMS history audit spec). The send endpoint normalises to
   *  E.164 server-side and returns 422 if the format is unrecoverable. */
  const [smsTo, setSmsTo] = useState<string>(lead.phone ?? "");
  const [smsTemplates, setSmsTemplates] = useState<CustomerMessageTemplate[]>([]);
  const [smsTemplatesLoaded, setSmsTemplatesLoaded] = useState(false);
  const [smsTplAnchor, setSmsTplAnchor] = useState<HTMLElement | null>(null);
  const [smsAiLoading, setSmsAiLoading] = useState(false);
  /** Tracks whether the current body came from the AI draft button so
   *  the eventual send can flag the SmsMessage row as ai_generated.
   *  Reset when the user edits the body or applies a template. */
  const [smsAiAssisted, setSmsAiAssisted] = useState(false);

  // Lazy-fetch customer_message templates the first time the user
  // opens the SMS composer. Keeps the page's initial load fast and
  // skips the round-trip for users who never send SMS.
  useEffect(() => {
    if (active === "sms" && !smsTemplatesLoaded) {
      fetchCustomerMessageTemplates()
        .then((rows) => setSmsTemplates(Array.isArray(rows) ? rows : []))
        .catch(() => setSmsTemplates([]))
        .finally(() => setSmsTemplatesLoaded(true));
    }
  }, [active, smsTemplatesLoaded]);

  const [saving, setSaving] = useState(false);

  const toast = (msg: string, kind: "success" | "error" = "success") => {
    if (kind === "success") {
      setSuccess(msg);
      setError(null);
      setTimeout(() => setSuccess(null), 3500);
    } else {
      setError(msg);
      setSuccess(null);
    }
  };

  const reset = (k: ActionKey) => {
    if (k === "call")      { setCallOutcome(""); setCallNotes(""); setCallTemplateId(null); }
    if (k === "note")      { setNoteText(""); setNoteTemplateId(null); }
    if (k === "email")     {
      setEmailTo(lead.email ?? "");
      setEmailSubject(""); setEmailBody(""); setEmailAttachments([]);
      setEmailTemplateId(null); setEmailAiAssisted(false);
    }
    if (k === "meeting")   { setMeetOutcome(""); setMeetNotes(""); setMeetTemplateId(null); }
    if (k === "follow_up") { setFuDate(""); setFuPriority("medium"); setFuNotes(""); }
    if (k === "sms")       { setSmsBody(""); setSmsAiAssisted(false); setSmsTo(lead.phone ?? ""); }
  };

  /* ── Save handlers ────────────────────────────────────────── */

  const saveCall = async () => {
    setSaving(true);
    try {
      await logContact({
        lead_id: lead.id,
        type: "call",
        outcome: callOutcome || undefined,
        notes: callNotes || undefined,
        template_id: callTemplateId,
      });
      const sug = CALL_SUGGEST[callOutcome];
      if (sug && sug.status !== lead.status) setSuggest(sug);
      reset("call");
      setActive(null);
      toast("Call saved");
      onChanged?.();
    } catch (e: any) {
      toast(e?.message || "Couldn't save call", "error");
    }
    setSaving(false);
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await addLeadNote(lead.id, noteText.trim(), { template_id: noteTemplateId });
      reset("note");
      setActive(null);
      toast("Note saved");
      onChanged?.();
    } catch (e: any) {
      toast(e?.message || "Couldn't save note", "error");
    }
    setSaving(false);
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || isBodyEmpty(emailBody) || !emailTo.trim()) return;
    setSaving(true);
    try {
      // Multipart whenever attachments OR template/ai tracking fields
      // are present — JSON path can't carry the file blobs and the
      // backend expects a single shape per request. Also use multipart
      // when the rep typed a custom recipient so we can carry `to`
      // through alongside the rest of the form.
      const overrideTo = emailTo.trim() !== (lead.email ?? "").trim();
      const useMultipart =
        emailAttachments.length > 0
        || emailTemplateId !== null
        || emailAiAssisted
        || overrideTo;
      let payload: FormData | { subject: string; body: string };
      if (useMultipart) {
        const fd = new FormData();
        fd.append("subject", emailSubject);
        fd.append("body", emailBody);
        if (overrideTo)               fd.append("to", emailTo.trim());
        if (emailTemplateId !== null) fd.append("template_id", String(emailTemplateId));
        if (emailAiAssisted)          fd.append("ai_assisted", "1");
        emailAttachments.forEach((f) => fd.append("attachments[]", f));
        payload = fd;
      } else {
        payload = { subject: emailSubject, body: emailBody };
      }
      await sendEmailFromLead(lead.id, payload);
      reset("email");
      setActive(null);
      toast("Email sent");
      onChanged?.();
    } catch (e: any) {
      toast(e?.message || "Couldn't send email", "error");
    }
    setSaving(false);
  };

  const saveMeeting = async () => {
    setSaving(true);
    try {
      await logContact({
        lead_id: lead.id,
        type: "meeting",
        outcome: meetOutcome || undefined,
        notes: meetNotes || undefined,
        template_id: meetTemplateId,
      });
      reset("meeting");
      setActive(null);
      toast("Meeting saved");
      onChanged?.();
    } catch (e: any) {
      toast(e?.message || "Couldn't save meeting", "error");
    }
    setSaving(false);
  };

  const createFollowUp = async () => {
    if (!fuDate) return;
    setSaving(true);
    try {
      await createTask({
        title: `Follow up with ${lead.name}`,
        lead_id: lead.id,
        assigned_to: fuAssignee === "" ? undefined : Number(fuAssignee),
        due_at: fuDate,
        priority: fuPriority,
        notes: fuNotes || undefined,
      });
      reset("follow_up");
      setActive(null);
      toast("Follow-up scheduled");
      onChanged?.();
    } catch (e: any) {
      toast(e?.message || "Couldn't schedule follow-up", "error");
    }
    setSaving(false);
  };

  /* ── Merge variable resolver (client-side) ─────────────────────
   * Substitutes {{token}} placeholders in a template body using
   * fields from the current lead. Unknown tokens are LEFT INTACT
   * so the rep notices the typo on first preview rather than the
   * customer receiving a half-resolved message. The supported
   * variable set is intentionally narrow — only the fields the
   * spec mentions plus a few obvious neighbors. */
  const resolveMergeVariables = (template: string): string => {
    const firstName = lead.name?.split(" ")[0] ?? "";
    const map: Record<string, string> = {
      customer_name:       lead.name ?? "",
      customer_first_name: firstName,
      lead_number:         lead.lead_number ?? "",
    };
    // Venue-only merge variables. Autism / tutoring messages must never
    // resolve {{event_type}} to a leftover legacy value or expose
    // {{hall_name}} / {{guest_count}} to a customer-facing SMS.
    if (leadConfig.supportsEventFields) {
      map.event_type = lead.event_type ?? "event";
      map.event_date = lead.event_date
        ? new Date(lead.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : "";
      map.hall_name       = lead.preferred_hall ?? "";
      map.preferred_hall  = lead.preferred_hall ?? "";
      map.guest_count     = lead.guest_count != null ? String(lead.guest_count) : "";
    } else {
      const s = resolveInquirySubject(lead);
      map.program            = s.program ?? "";
      map.program_of_interest = s.program ?? "";
      map.location           = s.location ?? "";
      map.child_name         = (lead.inquiry?.custom_fields as any)?.child_name?.value ?? "";
    }
    return template.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (full, key) => {
      const lower = String(key).toLowerCase();
      // Unknown token → leave intact so the rep can fix it.
      return Object.prototype.hasOwnProperty.call(map, lower) ? map[lower] : full;
    });
  };

  const applySmsTemplate = (tpl: CustomerMessageTemplate) => {
    const body = (tpl.body_text && tpl.body_text.trim()) || tpl.body || "";
    setSmsBody(resolveMergeVariables(body));
    setSmsAiAssisted(false);    // Template-applied bodies are NOT AI-flagged
    setSmsTplAnchor(null);      // Close the menu
  };

  const generateSmsDraft = async () => {
    setSmsAiLoading(true);
    try {
      const r = await generateSmsAiDraft(lead.id);
      setSmsBody(r.draft);
      setSmsAiAssisted(true);
    } catch (e: any) {
      toast(e?.message || "Couldn't generate AI draft", "error");
    }
    setSmsAiLoading(false);
  };

  const saveSms = async () => {
    const body  = smsBody.trim();
    const toRaw = smsTo.trim();
    if (!body || !toRaw) return;
    setSaving(true);
    try {
      const r = await sendSmsFromLead(lead.id, {
        message: body,
        // Editable destination — backend normalises to E.164 + dials
        // this number, not the lead's stored phone. When the rep
        // hasn't changed the field, this equals lead.phone so the
        // behaviour is identical to before.
        phone: toRaw,
        // Flag AI-drafted sends so the lead timeline can render
        // "AI-assisted SMS sent" — surfaces in audit + analytics later.
        ai_generated: smsAiAssisted,
      });
      // Branch on the REAL provider result. On a sync queue (default in
      // this environment) Twilio has already responded by the time we
      // get here, so the row is 'sent' or 'failed'. On an async queue
      // it's still 'queued' — we honor that too. Disabled = kill-switch.
      if (r.status === "failed") {
        // Don't close the form — the rep usually wants to edit and retry.
        toast(`SMS failed: ${r.error_message || "provider rejected the send"}`, "error");
      } else {
        reset("sms");
        setActive(null);
        if (r.status === "sent") {
          toast("SMS sent");
        } else if (r.status === "disabled") {
          toast("SMS recorded (sending is disabled in this environment)");
        } else {
          // 'queued' — async worker hasn't picked it up yet. Timeline
          // will upgrade to 'sent' / 'failed' once the worker runs.
          toast("SMS queued for delivery");
        }
        onChanged?.();
      }
    } catch (e: any) {
      toast(e?.message || "Couldn't send SMS", "error");
    }
    setSaving(false);
  };

  /* ── Smart-status suggestion handlers ─────────────────────── */

  const applySuggestion = async () => {
    if (!suggest) return;
    try {
      await updateLead(lead.id, { status: suggest.status });
      setSuggest(null);
      toast(`Status updated to ${suggest.status === "contacted" ? "Contacted" : "Lost"}`);
      onChanged?.();
    } catch (e: any) {
      toast(e?.message || "Couldn't update status", "error");
    }
  };

  /* ── Action tile config ───────────────────────────────────── */

  const tiles: { key: Exclude<ActionKey, null>; label: string; icon: React.ReactNode; color: string }[] = [
    { key: "call",      label: "Log Call",          icon: <CallIcon sx={{ fontSize: 18 }} />,         color: "#16a34a" },
    { key: "note",      label: "Add Note",          icon: <EditNoteIcon sx={{ fontSize: 18 }} />,     color: "#f59e0b" },
    { key: "email",     label: "Send Email",        icon: <MailOutlineIcon sx={{ fontSize: 18 }} />,  color: "#3b82f6" },
    { key: "sms",       label: "Send SMS",          icon: <SmsOutlinedIcon sx={{ fontSize: 18 }} />,  color: "#0891b2" },
    { key: "meeting",   label: "Log Meeting",       icon: <HandshakeIcon sx={{ fontSize: 18 }} />,    color: "#8b5cf6" },
    { key: "follow_up", label: "Schedule Follow-up",icon: <ScheduleIcon sx={{ fontSize: 18 }} />,     color: "#f97316" },
  ];

  // Read-only short-circuit. A previous assignee should NOT see any of
  // the touchpoint forms — there is no operational call-to-action left
  // for them on this lead. We keep the section header so the page reads
  // continuously, but the body collapses to a muted lock placeholder.
  if (readOnly) {
    return (
      <Box>
        <Box mb={1}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
            Quick Actions
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.1 }}>
            Log a touchpoint or schedule a follow-up — each saves to the Timeline.
          </Typography>
        </Box>
        <Box sx={{
          p: 1.4, borderRadius: "10px",
          bgcolor: "#f8fafc", border: "1px dashed #e2e8f0",
          display: "flex", alignItems: "center", gap: 1.2,
        }}>
          <LockIcon sx={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.3 }}>
            Read-only historical access — only the current assignee or an
            administrator can log calls, notes, emails, meetings, or
            follow-ups on this lead.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Section header ─────────────────────────────────────── */}
      <Box mb={1.4}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
          Quick Actions
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.1 }}>
          Log a touchpoint or schedule a follow-up — each saves to the Timeline.
        </Typography>
      </Box>

      {/* ── 5-tile grid ────────────────────────────────────────── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(6, 1fr)" },
        gap: 0.8,
        mb: active ? 1.4 : 0,
      }}>
        {tiles.map((t) => {
          const isActive = active === t.key;
          return (
            <Box
              key={t.key}
              role="button"
              onClick={() => setActive(isActive ? null : t.key)}
              sx={{
                cursor: "pointer", userSelect: "none",
                py: 1.1, px: 0.6, borderRadius: "10px",
                textAlign: "center",
                bgcolor: isActive ? `${t.color}10` : "#fff",
                border: `1.5px solid ${isActive ? t.color : "#eef0f4"}`,
                color: isActive ? t.color : "#64748b",
                transition: "all 0.15s",
                "&:hover": { borderColor: t.color, color: t.color, bgcolor: `${t.color}08` },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", mb: 0.4 }}>
                {t.icon}
              </Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, lineHeight: 1.2, letterSpacing: 0.2 }}>
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Inline form for the active tile ────────────────────── */}
      <Collapse in={!!active} unmountOnExit>
        <Box sx={{ p: 1.1, borderRadius: "10px", bgcolor: "#fafbfc", border: "1px solid #eef0f4" }}>

          {/* ── LOG CALL ────────────────────────────────────────── */}
          {active === "call" && (
            <Stack gap={0.9}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.7, textTransform: "uppercase" }}>
                Call Outcome
              </Typography>
              <Stack direction="row" gap={0.4} flexWrap="wrap">
                {OUTCOME_OPTIONS.map((o) => {
                  const sel = callOutcome === o.value;
                  return (
                    <Box
                      key={o.value}
                      role="button"
                      onClick={() => setCallOutcome(sel ? "" : o.value)}
                      sx={{
                        cursor: "pointer", userSelect: "none",
                        px: 0.9, py: 0.25, borderRadius: "7px",
                        fontSize: 10.5, fontWeight: sel ? 700 : 600,
                        color: sel ? NAVY : "#94a3b8",
                        border: sel ? `1.5px solid ${NAVY}` : "1px solid #eef0f4",
                        "&:hover": { color: NAVY, borderColor: "#cbd5e1" },
                      }}
                    >
                      {o.label}
                    </Box>
                  );
                })}
              </Stack>
              {/* Communication template toolbar — surfaces call-log
                  snippets (No Answer, Voicemail Left, Interested,
                  Quote Discussion, …). Applies the rendered body into
                  the notes textarea below; sets template_id so the
                  saved row carries usage attribution. */}
              <CommunicationTemplateToolbar
                templateType="call_log"
                leadId={lead.id}
                hasExistingBody={callNotes.trim().length > 0}
                onApplyTemplate={(applied) => {
                  setCallNotes(applied.body);
                  setCallTemplateId(applied.template_id);
                }}
                buttonLabel="Use Snippet"
              />
              <TextField
                placeholder="Notes from this call…"
                fullWidth size="small" multiline rows={2}
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
              />
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button size="small" onClick={() => setActive(null)} sx={{ textTransform: "none", fontSize: 11.5, py: 0.2 }}>Cancel</Button>
                <SaveButton onClick={saveCall} disabled={saving} label="Save Call" icon={<CallIcon sx={{ fontSize: 14 }} />} />
              </Stack>
            </Stack>
          )}

          {/* ── ADD NOTE ────────────────────────────────────────── */}
          {active === "note" && (
            <Stack gap={0.9}>
              <CommunicationTemplateToolbar
                templateType="internal_note"
                leadId={lead.id}
                hasExistingBody={noteText.trim().length > 0}
                onApplyTemplate={(applied) => {
                  setNoteText(applied.body);
                  setNoteTemplateId(applied.template_id);
                }}
                buttonLabel="Use Snippet"
              />
              <TextField
                placeholder="Internal note about this lead…"
                fullWidth size="small" multiline rows={3}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
              />
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button size="small" onClick={() => setActive(null)} sx={{ textTransform: "none", fontSize: 11.5, py: 0.2 }}>Cancel</Button>
                <SaveButton onClick={saveNote} disabled={saving || !noteText.trim()} label="Save Note" icon={<EditNoteIcon sx={{ fontSize: 14 }} />} />
              </Stack>
            </Stack>
          )}

          {/* ── SEND EMAIL ──────────────────────────────────────── */}
          {active === "email" && (
            <Stack gap={1.1}>
              {!lead.email && (
                <Alert severity="info" sx={{ fontSize: 11.5, py: 0.3, borderRadius: 1.5 }}>
                  No email on file for this lead — type one in the <strong>To</strong> field below.
                </Alert>
              )}

              {/* To — defaults to the lead's email on file; the rep can
                  override it per-send (e.g. CC an assistant, redirect
                  to a corporate address). Override gets persisted onto
                  the email row + thread without touching the lead. */}
              <TextField
                label="To" fullWidth size="small"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="customer@example.com"
                helperText={
                  emailTo.trim() && lead.email && emailTo.trim() !== lead.email
                    ? `Sending to a different address than the lead's on-file email (${lead.email}).`
                    : " "
                }
                FormHelperTextProps={{ sx: { fontSize: 10, mt: 0.2 } }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "#fff" } }}
              />

              {/* Shared composer toolbar — identical to the one in the
                  Email Hub Reply modal. Use Template / AI Draft /
                  quick chips. Variables auto-resolve against THIS
                  lead since we pass its id. */}
              <EmailComposerToolbar
                leadId={lead.id}
                subject={emailSubject}
                hasExistingBody={!isBodyEmpty(emailBody)}
                onApplyTemplate={(applied) => {
                  setEmailSubject(applied.subject);
                  setEmailBody(applied.body);
                  setEmailTemplateId(applied.template_id);
                  setEmailAiAssisted(false);
                }}
                onApplyAIDraft={(result) => {
                  setEmailBody(result.body);
                  setEmailTemplateId(null);
                  setEmailAiAssisted(true);
                }}
              />

              <TextField
                label="Subject" fullWidth size="small"
                value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "#fff" } }}
              />
              {/* Rich text editor — template HTML renders properly
                  instead of appearing as visible <p> tags, and Quill
                  emits clean HTML on send. Backend normalizer covers
                  any plain-text edge case. */}
              <Box sx={{
                "& .ql-toolbar": { borderTopLeftRadius: 6, borderTopRightRadius: 6, borderColor: "rgba(0,0,0,0.23)", bgcolor: "#fafbfc" },
                "& .ql-container": { borderBottomLeftRadius: 6, borderBottomRightRadius: 6, borderColor: "rgba(0,0,0,0.23)", fontSize: 13, fontFamily: "inherit", minHeight: 140, bgcolor: "#fff" },
                "& .ql-editor": { minHeight: 140 },
              }}>
                <ReactQuill
                  theme="snow"
                  value={emailBody}
                  onChange={setEmailBody}
                  placeholder="Message"
                />
              </Box>

              {/* Active tracking labels — surface what'll be persisted on send. */}
              {(emailTemplateId || emailAiAssisted) && (
                <Stack direction="row" gap={0.5}>
                  {emailTemplateId && (
                    <Chip size="small" label="Template applied"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#FBF7EE", color: NAVY }} />
                  )}
                  {emailAiAssisted && (
                    <Chip size="small" label="AI-assisted"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#F5EFE0", color: "#A8893E" }} />
                  )}
                </Stack>
              )}

              {/* Shared drag-and-drop attachment area. */}
              <AttachmentDropzone
                files={emailAttachments}
                onChange={setEmailAttachments}
                maxFiles={5}
                maxBytesEach={10 * 1024 * 1024}
              />

              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button size="small" onClick={() => setActive(null)} sx={{ textTransform: "none", fontSize: 11.5 }}>Cancel</Button>
                <SaveButton
                  onClick={sendEmail}
                  disabled={saving || !emailTo.trim() || !emailSubject.trim() || isBodyEmpty(emailBody)}
                  label="Send Email"
                  icon={<SendIcon sx={{ fontSize: 14 }} />}
                />
              </Stack>
            </Stack>
          )}

          {/* ── SEND SMS ────────────────────────────────────────
              Phase 2.A composer: phone preview + plain-text body +
              live char/segment counter + Send button. Phase 2.B adds
              templates; Phase 2.C adds an AI draft button. Backend
              handles E.164 normalisation, queue dispatch, timeline
              emit, and ownership check. */}
          {active === "sms" && (() => {
            const len  = smsBody.length;
            const segs = len === 0 ? 0 : Math.max(1, Math.ceil(len / 160));
            const overSingleSeg = len > 160;
            const overMax = len > 1600;
            // "noPhone" now means "the destination field is empty",
            // NOT "the lead has no stored phone" — the rep may have
            // changed the number to a secondary contact. Empty trim
            // disables the Send button.
            const noPhone = !smsTo.trim();
            const phoneChanged = (lead.phone ?? "").trim() !== smsTo.trim();
            return (
              <Stack gap={0.9}>
                {/* Editable destination. Pre-fills from lead.phone but
                    the rep can correct typos, dial a spouse / alternate
                    contact, or punch in a one-off callback number.
                    Backend normalises to E.164 on send + returns 422
                    when the input can't be coerced into a dialable
                    number. The phone-changed pill makes any override
                    obvious so it doesn't happen by accident. */}
                <Stack direction="row" gap={1} alignItems="center" sx={{
                  px: 1.1, py: 0.5, borderRadius: 1.5,
                  bgcolor: "#f8fafc", border: "1px solid #e2e8f0",
                }}>
                  <SmsOutlinedIcon sx={{ fontSize: 16, color: "#0891b2", flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{
                      fontSize: 9, fontWeight: 800, color: "#94a3b8",
                      letterSpacing: 0.7, textTransform: "uppercase",
                    }}>
                      To
                    </Typography>
                    <TextField
                      value={smsTo}
                      onChange={(e) => setSmsTo(e.target.value)}
                      placeholder="+1 (647) 555-1234"
                      variant="standard"
                      fullWidth
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          fontSize: 12.5, fontWeight: 700, color: "#0f172a",
                          fontFamily: "'JetBrains Mono','SF Mono',Menlo,Consolas,monospace",
                          lineHeight: 1.2,
                          "& input": { p: 0, height: "auto" },
                          "& input::placeholder": { color: "#cbd5e1", fontWeight: 600, opacity: 1 },
                        },
                      }}
                      inputProps={{ "aria-label": "SMS destination phone" }}
                    />
                  </Box>
                  {phoneChanged && (
                    <Chip
                      label="Changed"
                      size="small"
                      onClick={() => setSmsTo(lead.phone ?? "")}
                      sx={{
                        height: 18, fontSize: 9, fontWeight: 700,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        bgcolor: "#fef3c7", color: "#b45309",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#fde68a" },
                      }}
                      title="Click to restore the lead's primary phone"
                    />
                  )}
                </Stack>
                {/* Toolbar — Use Template + ✨ AI Draft. Both fill the
                    textarea; user can edit before sending. AI-assisted
                    sends get flagged in the timeline. Templates apply
                    merge variables CLIENT-SIDE from the lead context. */}
                <Stack direction="row" gap={0.7} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ArticleOutlinedIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => setSmsTplAnchor(e.currentTarget)}
                    disabled={noPhone || smsAiLoading || saving || smsTemplates.length === 0}
                    sx={{
                      textTransform: "none", fontSize: 11, fontWeight: 700,
                      color: "#0891b2", borderColor: "#a5f3fc",
                      "&:hover": { bgcolor: "#ecfeff", borderColor: "#06b6d4" },
                    }}
                  >
                    Use Template
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={smsAiLoading
                      ? <CircularProgress size={12} sx={{ color: "inherit" }} />
                      : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                    onClick={generateSmsDraft}
                    disabled={noPhone || smsAiLoading || saving}
                    sx={{
                      textTransform: "none", fontSize: 11, fontWeight: 700,
                      color: "#7c3aed", borderColor: "#ddd6fe",
                      "&:hover": { bgcolor: "#f5f3ff", borderColor: "#a78bfa" },
                    }}
                  >
                    {smsAiLoading ? "Drafting…" : "AI Draft"}
                  </Button>
                  {smsAiAssisted && !smsAiLoading && (
                    <Chip
                      label="AI-assisted"
                      size="small"
                      sx={{
                        height: 18, fontSize: 9, fontWeight: 700,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        bgcolor: "#f5f3ff", color: "#6d28d9",
                      }}
                    />
                  )}
                </Stack>
                <Menu
                  anchorEl={smsTplAnchor}
                  open={Boolean(smsTplAnchor)}
                  onClose={() => setSmsTplAnchor(null)}
                  PaperProps={{ sx: { maxWidth: 360, maxHeight: 360 } }}
                >
                  {smsTemplates.length === 0 ? (
                    <MenuItem disabled>
                      <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>
                        No customer-message templates yet
                      </Typography>
                    </MenuItem>
                  ) : (
                    smsTemplates.map((tpl) => (
                      <MenuItem
                        key={tpl.id}
                        onClick={() => applySmsTemplate(tpl)}
                        sx={{ alignItems: "flex-start", py: 0.7 }}
                      >
                        <ListItemText
                          primary={
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                              {tpl.name}
                            </Typography>
                          }
                          secondary={
                            <Typography sx={{
                              fontSize: 10.5, color: "#64748b",
                              whiteSpace: "normal", lineHeight: 1.35,
                              mt: 0.2,
                            }}>
                              {(tpl.body_text || tpl.body || "").slice(0, 110)}
                              {((tpl.body_text || tpl.body || "").length > 110) ? "…" : ""}
                            </Typography>
                          }
                        />
                      </MenuItem>
                    ))
                  )}
                </Menu>

                <TextField
                  placeholder={`Hi ${lead.name?.split(" ")[0] ?? ""}, …`}
                  fullWidth size="small" multiline rows={3}
                  value={smsBody}
                  // Manual edits clear the AI-assisted badge — the rep
                  // has now authored the message themselves and the
                  // timeline shouldn't claim AI authorship.
                  onChange={(e) => { setSmsBody(e.target.value); if (smsAiAssisted) setSmsAiAssisted(false); }}
                  disabled={noPhone || smsAiLoading}
                  inputProps={{ maxLength: 1600 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
                />
                {/* Live counters — 160-char single-segment boundary +
                    1600-char hard cap. Color shifts as the message grows. */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.4 }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 600,
                    color: overMax ? "#dc2626" : overSingleSeg ? "#f59e0b" : "#64748b",
                    letterSpacing: 0.3,
                  }}>
                    {len} / 1600 chars · {segs} {segs === 1 ? "segment" : "segments"}
                  </Typography>
                  {noPhone && (
                    <Typography sx={{ fontSize: 10, color: "#dc2626", fontWeight: 600 }}>
                      No phone — add one to the lead first
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setActive(null)} sx={{ textTransform: "none", fontSize: 11.5, py: 0.2 }}>
                    Cancel
                  </Button>
                  <SaveButton
                    onClick={saveSms}
                    disabled={saving || noPhone || !smsBody.trim() || overMax}
                    label="Send SMS"
                    icon={<SendIcon sx={{ fontSize: 14 }} />}
                  />
                </Stack>
              </Stack>
            );
          })()}

          {/* ── LOG MEETING ─────────────────────────────────────── */}
          {active === "meeting" && (
            <Stack gap={0.9}>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.7, textTransform: "uppercase" }}>
                Meeting Outcome
              </Typography>
              <Stack direction="row" gap={0.4} flexWrap="wrap">
                {MEETING_OUTCOMES.map((o) => {
                  const sel = meetOutcome === o.value;
                  return (
                    <Box
                      key={o.value}
                      role="button"
                      onClick={() => setMeetOutcome(sel ? "" : o.value)}
                      sx={{
                        cursor: "pointer", userSelect: "none",
                        px: 0.9, py: 0.25, borderRadius: "7px",
                        fontSize: 10.5, fontWeight: sel ? 700 : 600,
                        color: sel ? NAVY : "#94a3b8",
                        border: sel ? `1.5px solid ${NAVY}` : "1px solid #eef0f4",
                        "&:hover": { color: NAVY, borderColor: "#cbd5e1" },
                      }}
                    >
                      {o.label}
                    </Box>
                  );
                })}
              </Stack>
              <CommunicationTemplateToolbar
                templateType="meeting_note"
                leadId={lead.id}
                hasExistingBody={meetNotes.trim().length > 0}
                onApplyTemplate={(applied) => {
                  setMeetNotes(applied.body);
                  setMeetTemplateId(applied.template_id);
                }}
                buttonLabel="Use Snippet"
              />
              <TextField
                placeholder="Meeting notes — what was discussed, next steps…"
                fullWidth size="small" multiline rows={2}
                value={meetNotes}
                onChange={(e) => setMeetNotes(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
              />
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button size="small" onClick={() => setActive(null)} sx={{ textTransform: "none", fontSize: 11.5, py: 0.2 }}>Cancel</Button>
                <SaveButton onClick={saveMeeting} disabled={saving} label="Save Meeting" icon={<HandshakeIcon sx={{ fontSize: 14 }} />} />
              </Stack>
            </Stack>
          )}

          {/* ── SCHEDULE FOLLOW-UP ──────────────────────────────── */}
          {active === "follow_up" && (
            <Stack gap={0.9}>
              <PremiumDateTimePicker
                label="Follow-up Date & Time *"
                value={fuDate}
                onChange={(v) => setFuDate(v)}
              />
              <Stack direction="row" gap={1}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.7, textTransform: "uppercase", mb: 0.4 }}>
                    Priority
                  </Typography>
                  <Stack direction="row" gap={0.3}>
                    {(["low", "medium", "high", "urgent"] as const).map((p) => {
                      const sel = fuPriority === p;
                      const colors: Record<string, string> = { low: "#94a3b8", medium: "#3b82f6", high: "#f59e0b", urgent: "#ef4444" };
                      return (
                        <Box
                          key={p}
                          role="button"
                          onClick={() => setFuPriority(p)}
                          sx={{
                            flex: 1, textAlign: "center", cursor: "pointer", userSelect: "none",
                            py: 0.3, borderRadius: "7px",
                            fontSize: 10, fontWeight: sel ? 700 : 600, textTransform: "capitalize",
                            color: sel ? colors[p] : "#94a3b8",
                            border: sel ? `1.5px solid ${colors[p]}` : "1px solid #eef0f4",
                            "&:hover": { color: colors[p], borderColor: "#cbd5e1" },
                          }}
                        >
                          {p}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.7, textTransform: "uppercase", mb: 0.4 }}>
                    Assignee
                  </Typography>
                  <select
                    value={fuAssignee === "" ? "" : String(fuAssignee)}
                    onChange={(e) => setFuAssignee(e.target.value === "" ? "" : Number(e.target.value))}
                    style={{
                      width: "100%", padding: "5px 8px", fontSize: 12,
                      border: "1px solid #eef0f4", borderRadius: 7, background: "#fff",
                      color: NAVY, fontWeight: 600,
                    }}
                  >
                    <option value="">Lead owner</option>
                    {teamUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.display_name
                          || [u.first_name, u.last_name].filter(Boolean).join(" ")
                          || u.username}
                      </option>
                    ))}
                  </select>
                </Box>
              </Stack>
              <TextField
                placeholder="What's this follow-up about?"
                fullWidth size="small" multiline rows={2}
                value={fuNotes}
                onChange={(e) => setFuNotes(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
              />
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button size="small" onClick={() => setActive(null)} sx={{ textTransform: "none", fontSize: 11.5 }}>Cancel</Button>
                <SaveButton onClick={createFollowUp} disabled={saving || !fuDate} label="Create Follow-up" icon={<ScheduleIcon sx={{ fontSize: 14 }} />} />
              </Stack>
            </Stack>
          )}
        </Box>
      </Collapse>

      {/* ── Smart status suggestion banner ─────────────────────── */}
      {suggest && (
        <Alert
          severity="info"
          sx={{ mt: 1.4, borderRadius: 1.5, fontSize: 12, py: 0.5, alignItems: "center" }}
          action={
            <Stack direction="row" gap={0.5}>
              <Button size="small" onClick={() => setSuggest(null)} sx={{ textTransform: "none", fontSize: 11.5, color: "#64748b" }}>
                Dismiss
              </Button>
              <Button size="small" variant="contained" onClick={applySuggestion}
                sx={{ textTransform: "none", fontSize: 11.5, bgcolor: NAVY, "&:hover": { bgcolor: "#1e3a5f" } }}>
                Yes, update
              </Button>
            </Stack>
          }
        >
          {suggest.label}
        </Alert>
      )}

      {/* ── Result feedback — floating Snackbar ──────────────────
          Previously rendered as inline Alerts BELOW the action grid.
          That broke for actions that collapse their form on success
          (Send SMS, Save Note, etc.) — the form vanished + the Alert
          appeared off-screen + the user saw nothing. A Snackbar
          floats at the bottom-right of the viewport, persists 4s,
          and is impossible to miss. */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccess(null)}
          sx={{ borderRadius: 1.5, fontSize: 12.5, fontWeight: 600, alignItems: "center" }}
        >
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setError(null)}
          sx={{ borderRadius: 1.5, fontSize: 12.5, fontWeight: 600, alignItems: "center" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ── Save-button helper — consistent styling across all forms ─────── */
function SaveButton({
  onClick, disabled, label, icon,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      startIcon={icon}
      variant="contained"
      disableElevation
      sx={{
        bgcolor: NAVY, fontWeight: 700, borderRadius: "8px", py: 0.6, px: 2,
        fontSize: 11.5, letterSpacing: 0.3, textTransform: "none",
        "&:hover": { bgcolor: "#142b46" },
        "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff" },
      }}
    >
      {label}
    </Button>
  );
}
